import { useState, useRef, useEffect, useCallback } from 'react'
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, isSameDay, isSameMonth, isToday,
  format, addMonths, subMonths
} from 'date-fns'

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

export default function DayDividerCalendar({
  label, sectionDate, daysWithMessages, isExpanded, onToggle, onNavigateToDate
}) {
  const baseDate = new Date(sectionDate)
  const [viewMonth, setViewMonth] = useState(baseDate)
  const calRef = useRef(null)
  const contentRef = useRef(null)
  const pillRef = useRef(null)
  const [contentHeight, setContentHeight] = useState(0)
  const [pillWidth, setPillWidth] = useState(0)
  const [closing, setClosing] = useState(false)

  // Measure pill width once for smooth close animation
  useEffect(() => {
    if (pillRef.current && !isExpanded && !closing) {
      setPillWidth(pillRef.current.offsetWidth)
    }
  }, [label])

  // Reset viewMonth when expanding to the section's date
  useEffect(() => {
    if (isExpanded) setViewMonth(new Date(sectionDate))
  }, [isExpanded, sectionDate])

  // Measure content height for smooth morph animation
  useEffect(() => {
    if (isExpanded && contentRef.current) {
      const measure = () => setContentHeight(contentRef.current.scrollHeight)
      measure()
      const obs = new ResizeObserver(measure)
      obs.observe(contentRef.current)
      return () => obs.disconnect()
    }
  }, [isExpanded, viewMonth])

  // Close on Escape
  useEffect(() => {
    if (!isExpanded) return
    const handler = (e) => { if (e.key === 'Escape') handleClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [isExpanded, onToggle])

  // Close on click outside
  useEffect(() => {
    if (!isExpanded) return
    const handler = (e) => {
      if (calRef.current && !calRef.current.contains(e.target)) handleClose()
    }
    // Delay to avoid the opening click from immediately closing
    const timer = setTimeout(() => document.addEventListener('mousedown', handler), 10)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('mousedown', handler)
    }
  }, [isExpanded, onToggle])

  const monthStart = startOfMonth(viewMonth)
  const monthEnd = endOfMonth(viewMonth)
  const calStart = startOfWeek(monthStart)
  const calEnd = endOfWeek(monthEnd)
  const days = eachDayOfInterval({ start: calStart, end: calEnd })

  const handleClose = useCallback(() => {
    setClosing(true)
    setTimeout(() => {
      setClosing(false)
      onToggle()
    }, 350)
  }, [onToggle])

  const handleDayClick = useCallback((day) => {
    const dateStr = day.toDateString()
    if (daysWithMessages.has(dateStr)) {
      onNavigateToDate(dateStr)
    }
  }, [daysWithMessages, onNavigateToDate])

  return (
    <div className="day-divider" ref={calRef} style={(isExpanded || closing) ? { zIndex: 50 } : undefined}>
      <div
        className={`day-island${isExpanded || closing ? ' day-island-expanded' : ''}${closing ? ' day-island-closing' : ''}`}
        style={
          closing ? { height: 30, width: pillWidth || undefined } :
          isExpanded ? { height: contentHeight + 30 } :
          undefined
        }
      >
        <button ref={pillRef} className="day-island-pill" onClick={isExpanded ? handleClose : onToggle}>
          <span>{label}</span>
        </button>

        <div className="day-island-content" ref={contentRef}>
          {(isExpanded || closing) && (
            <>
              <div className="day-cal-header">
                <button className="day-cal-nav" onClick={(e) => { e.stopPropagation(); setViewMonth(m => subMonths(m, 1)) }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </button>
                <span className="day-cal-title">{format(viewMonth, 'MMMM yyyy')}</span>
                <button className="day-cal-nav" onClick={(e) => { e.stopPropagation(); setViewMonth(m => addMonths(m, 1)) }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              </div>

              <div className="day-cal-grid">
                {WEEKDAYS.map(d => (
                  <span key={d} className="day-cal-weekday">{d}</span>
                ))}
                {days.map((day, i) => {
                  const inMonth = isSameMonth(day, viewMonth)
                  const hasMsg = daysWithMessages.has(day.toDateString())
                  const today = isToday(day)
                  const selected = isSameDay(day, baseDate)
                  return (
                    <button
                      key={i}
                      className={[
                        'day-cal-day',
                        !inMonth && 'outside',
                        hasMsg && 'has-msgs',
                        today && 'today',
                        selected && 'selected',
                      ].filter(Boolean).join(' ')}
                      onClick={(e) => { e.stopPropagation(); handleDayClick(day) }}
                      disabled={!inMonth || !hasMsg}
                    >
                      <span>{format(day, 'd')}</span>
                      {hasMsg && inMonth && <span className="day-cal-dot" />}
                    </button>
                  )
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
